(() => {
  const CONFIG_ID = 'ProductEngravingConfig';
  const ENGRAVING_NOTE_START = '[Engraving]';
  const ENGRAVING_NOTE_END = '[/Engraving]';
  const NOTE_MAX_LENGTH = 5000;

  function ensureEngravingCartNoteHelpers() {
    if (typeof window.syncEngravingCartNote === 'function') return;

    function buildEngravingNoteBlock(cart) {
      const lines = [];

      for (const item of cart.items || []) {
        const props = item.properties || {};
        const text = String(props.Text || '').trim();
        const image = String(props.Image || '').trim();
        const value = text || image;
        if (!value) continue;

        const type = text ? 'Text' : 'Image';
        const title = item.product_title || item.title || 'Product';
        const qtySuffix = item.quantity > 1 ? ` x${item.quantity}` : '';
        lines.push(`${title}${qtySuffix} (${type}): ${value}`);
      }

      if (!lines.length) return '';
      return `${ENGRAVING_NOTE_START}\n${lines.join('\n')}\n${ENGRAVING_NOTE_END}`;
    }

    function mergeEngravingNote(existingNote, block) {
      const note = String(existingNote || '')
        .replace(/\n?\[Engraving\][\s\S]*?\[\/Engraving\]\n?/g, '')
        .trim();

      if (!block) return note;

      let merged = note ? `${note}\n\n${block}` : block;
      if (merged.length <= NOTE_MAX_LENGTH) return merged;

      const separatorLength = note ? 2 : 0;
      const maxBlockLength = NOTE_MAX_LENGTH - note.length - separatorLength;
      if (maxBlockLength < ENGRAVING_NOTE_START.length + ENGRAVING_NOTE_END.length + 3) {
        return note.slice(0, NOTE_MAX_LENGTH);
      }

      const trimmedBlock = block.slice(0, maxBlockLength);
      merged = note ? `${note}\n\n${trimmedBlock}` : trimmedBlock;
      return merged.slice(0, NOTE_MAX_LENGTH);
    }

    async function syncEngravingCartNote(cart) {
      if (!cart) return;

      const nextNote = mergeEngravingNote(cart.note, buildEngravingNoteBlock(cart));
      const currentNote = cart.note || '';
      if (nextNote === currentNote) return;

      const body = JSON.stringify({ note: nextNote });
      const updateUrl = window.routes?.cart_update_url || '/cart/update.js';
      await fetch(updateUrl, { ...fetchConfig(), ...{ body } });
    }

    async function syncEngravingCartNoteFromCart() {
      const cartUrl = `${window.routes?.cart_url || '/cart'}.js`;
      const response = await fetch(cartUrl);
      if (!response.ok) return;
      const cart = await response.json();
      await syncEngravingCartNote(cart);
    }

    window.syncEngravingCartNote = syncEngravingCartNote;
    window.syncEngravingCartNoteFromCart = syncEngravingCartNoteFromCart;
  }

  ensureEngravingCartNoteHelpers();

  function readConfig() {
    const el = document.getElementById(CONFIG_ID);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      console.error('ProductEngraving: invalid config', error);
      return null;
    }
  }

  function getRoot() {
    return document.querySelector('[data-product-engraving]');
  }

  function getForm(root) {
    if (!root) return null;
    const formId = root.dataset.formId;
    if (formId) {
      const byId = document.getElementById(formId);
      if (byId) return byId;
    }
    return (
      document.querySelector('product-form form') ||
      document.querySelector('#bourbon-product-form') ||
      document.querySelector('form[action*="/cart/add"]')
    );
  }

  function setError(root, message) {
    const errorEl = root?.querySelector('[data-engraving-error]');
    if (!errorEl) return;
    if (message) {
      errorEl.hidden = false;
      errorEl.textContent = message;
    } else {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }
  }

  function isEngravingActive() {
    const uid = document.getElementById('engraving_uid');
    return Boolean(uid && uid.getAttribute('name') === 'properties[_uid]' && uid.value);
  }

  function refreshUid() {
    const uid = document.getElementById('engraving_uid');
    if (!uid) return null;
    if (uid.getAttribute('name') === 'properties[_uid]') {
      uid.value = String(Date.now());
    }
    return uid.value;
  }

  function collectProperties(form) {
    const properties = {};
    const formData = new FormData(form);

    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[([^\]]+)\]$/);
      if (match) {
        properties[match[1]] = value;
      }
    }

    return properties;
  }

  function buildItemsPayload(form) {
    const formData = new FormData(form);
    const id = Number(formData.get('id'));
    const quantity = Number(formData.get('quantity') || 1) || 1;
    const properties = collectProperties(form);
    const items = [{ id, quantity, properties }];

    if (!isEngravingActive()) {
      return { items };
    }

    const uidInput = document.getElementById('engraving_uid');
    const engravingVariantId = Number(uidInput?.getAttribute('data-engraving-variant'));
    const uid = refreshUid() || properties._uid;

    if (engravingVariantId && uid) {
      items[0].properties = { ...properties, _uid: uid };
      items.push({
        id: engravingVariantId,
        quantity,
        properties: { _uid: uid },
      });
    }

    return { items };
  }

  function buildPairedFormData(form) {
    const payload = buildItemsPayload(form);
    const formData = new FormData();

    payload.items.forEach((item, index) => {
      formData.append(`items[${index}][id]`, String(item.id));
      formData.append(`items[${index}][quantity]`, String(item.quantity));
      Object.entries(item.properties || {}).forEach(([key, value]) => {
        formData.append(`items[${index}][properties][${key}]`, value);
      });
    });

    return formData;
  }

  function hideDynamicCheckout(form) {
    if (!form) return;
    const wrappers = form.querySelectorAll(
      '.shopify-payment-button, [data-shopify="payment-button"]'
    );
    wrappers.forEach((el) => {
      el.style.display = isEngravingActive() ? 'none' : '';
    });
  }

  function markSubmitted(box, previewText, config) {
    box.classList.remove('active');
    box.classList.add('submitted');
    box.querySelector('.engraving-btn')?.classList.add('submitted');

    const title = box.querySelector('.custom-title');
    if (title) title.textContent = config.strings.applied;

    const preview = box.querySelector('.text-or-file');
    if (preview) {
      preview.textContent = previewText;
      preview.hidden = false;
      preview.style.display = 'inline-block';
    }

    box.querySelectorAll('.edit-engraving, .remove-engraving').forEach((el) => {
      el.style.display = 'inline';
    });
  }

  function clearSubmitted(box, config) {
    const title = box.querySelector('.custom-title');
    if (title) title.textContent = config.strings.add;

    const preview = box.querySelector('.text-or-file');
    if (preview) {
      preview.textContent = '';
      preview.hidden = true;
      preview.style.display = 'none';
    }

    box.classList.remove('submitted', 'active');
    box.querySelector('.engraving-btn')?.classList.remove('submitted');
    box.querySelectorAll('.edit-engraving, .remove-engraving').forEach((el) => {
      el.style.display = 'none';
    });

    const textInput = document.getElementById('engraving_text');
    const uploadInput = document.getElementById('engraving_upload');
    const urlInput = document.getElementById('engraving_upload_cloudinary_url');
    const uidInput = document.getElementById('engraving_uid');

    textInput?.removeAttribute('name');
    if (textInput) textInput.value = '';
    uploadInput?.removeAttribute('name');
    if (uploadInput) uploadInput.value = '';
    urlInput?.removeAttribute('name');
    if (urlInput) urlInput.value = '';
    uidInput?.removeAttribute('name');
    if (uidInput) uidInput.value = '';
  }

  async function uploadToCloudinary(file, config, submitButtons) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', config.uploadPreset);

    submitButtons.forEach((btn) => btn.setAttribute('aria-busy', 'true'));

    try {
      const response = await fetch(`https://api.cloudinary.com/v1_1/${config.cloudName}/image/upload`, {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      if (!response.ok || !data.secure_url) {
        throw new Error(data.error?.message || 'Upload failed');
      }
      return data.secure_url;
    } finally {
      submitButtons.forEach((btn) => btn.removeAttribute('aria-busy'));
    }
  }

  function initUi(root, config) {
    if (!root || root.dataset.engravingUiBound === 'true') return;
    root.dataset.engravingUiBound = 'true';

    const box = root.querySelector('.engraving-box');
    const form = getForm(root);
    if (!box) return;

    const textTab = root.querySelector('#textEngraving');
    const logoTab = root.querySelector('#logoEngraving');
    const textFields = root.querySelector('#engravingTextFields');
    const logoFields = root.querySelector('#engravingLogoFields');
    const textInput = root.querySelector('#engraving_text');
    const saveTextBtn = root.querySelector('#text_submit_button');
    const imageSelectBtn = root.querySelector('#engraving_image_select');
    const uploadInput = root.querySelector('#engraving_upload');
    const urlInput = root.querySelector('#engraving_upload_cloudinary_url');
    const uidInput = root.querySelector('#engraving_uid');
    const firstPart = root.querySelector('.engraving-first-part');
    const editBtn = root.querySelector('.edit-engraving');
    const removeBtn = root.querySelector('.remove-engraving');

    const submitButtons = form
      ? Array.from(form.querySelectorAll('[type="submit"], .product-form__submit, .add-to-cart-btn'))
      : [];

    const activateTab = (mode) => {
      const isText = mode === 'text';
      textTab?.classList.toggle('active', isText);
      logoTab?.classList.toggle('active', !isText);
      textTab?.setAttribute('aria-selected', String(isText));
      logoTab?.setAttribute('aria-selected', String(!isText));
      textFields?.classList.toggle('active', isText);
      logoFields?.classList.toggle('active', !isText);
      if (logoFields) logoFields.hidden = isText;
      if (textFields) textFields.hidden = !isText;
    };

    const toggleOpen = () => {
      box.classList.toggle('active');
      const isActive = box.classList.contains('active');
      root.querySelectorAll('.edit-engraving, .remove-engraving').forEach((el) => {
        el.style.display = !isActive && box.classList.contains('submitted') ? 'inline' : 'none';
      });
      setError(root, '');
    };

    firstPart?.addEventListener('click', toggleOpen);
    editBtn?.addEventListener('click', (event) => {
      event.preventDefault();
      toggleOpen();
    });

    textTab?.addEventListener('click', () => activateTab('text'));
    logoTab?.addEventListener('click', () => activateTab('logo'));

    saveTextBtn?.addEventListener('click', (event) => {
      event.preventDefault();
      const value = textInput?.value?.trim();
      if (!value) {
        setError(root, config.strings.textRequired);
        return;
      }
      setError(root, '');
      textInput.setAttribute('name', 'properties[Text]');
      urlInput?.removeAttribute('name');
      uidInput?.setAttribute('name', 'properties[_uid]');
      uidInput.value = String(Date.now());
      markSubmitted(box, value, config);
      hideDynamicCheckout(form);
    });

    imageSelectBtn?.addEventListener('click', (event) => {
      event.preventDefault();
      uploadInput?.click();
    });

    uploadInput?.addEventListener('change', async () => {
      const file = uploadInput.files?.[0];
      if (!file) return;

      if (!config.cloudName || !config.uploadPreset) {
        setError(root, config.strings.uploadError);
        return;
      }

      setError(root, config.strings.uploading || '');
      try {
        const secureUrl = await uploadToCloudinary(file, config, submitButtons);
        urlInput.value = secureUrl;
        urlInput.setAttribute('name', 'properties[Image]');
        textInput?.removeAttribute('name');
        uidInput?.setAttribute('name', 'properties[_uid]');
        uidInput.value = String(Date.now());
        markSubmitted(box, file.name, config);
        setError(root, '');
        hideDynamicCheckout(form);
      } catch (error) {
        console.error('ProductEngraving upload error:', error);
        clearSubmitted(box, config);
        setError(root, config.strings.uploadError);
        hideDynamicCheckout(form);
      }
    });

    removeBtn?.addEventListener('click', (event) => {
      event.preventDefault();
      clearSubmitted(box, config);
      setError(root, '');
      hideDynamicCheckout(form);
    });

    activateTab('text');
    hideDynamicCheckout(form);
  }

  function attachStandaloneFormHandler(root) {
    const form = getForm(root);
    if (!form || form.dataset.engravingBound === 'true') return;
    if (form.closest('product-form')) return;
    // Alma PDPs are handled by pdp.js (add → /cart, buy now → checkout).
    // Use getAttribute('id'): form.id is shadowed by <input name="id">.
    if (form.getAttribute('id') === 'product-form' && form.querySelector('.add-to-cart-btn')) return;

    form.dataset.engravingBound = 'true';
    let submitting = false;
    form.addEventListener('submit', async (event) => {
      if (!isEngravingActive()) return;

      event.preventDefault();
      if (submitting) return;
      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      submitting = true;
      const submitButtons = Array.from(form.querySelectorAll('[type="submit"]'));
      submitButtons.forEach((btn) => {
        btn.setAttribute('disabled', 'disabled');
        btn.setAttribute('aria-busy', 'true');
      });

      try {
        const payload = buildItemsPayload(form);
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        const body = { ...payload };
        if (cart?.getSectionsToRender) {
          body.sections = cart
            .getSectionsToRender()
            .map((section) => section.id)
            .join(',');
          body.sections_url = window.location.pathname;
        }

        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        });
        const data = await response.json();

        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Unable to add to cart');
        }

        if (window.syncEngravingCartNoteFromCart) {
          await window.syncEngravingCartNoteFromCart();
        }

        if (cart?.renderContents) {
          cart.renderContents(data);
        } else {
          window.location = window.routes?.cart_url || '/cart';
        }
      } catch (error) {
        console.error('ProductEngraving add error:', error);
        setError(root, error.message);
      } finally {
        submitting = false;
        submitButtons.forEach((btn) => {
          btn.removeAttribute('disabled');
          btn.removeAttribute('aria-busy');
        });
      }
    });
  }

  function initShogunSupport(config) {
    const existing = getRoot();
    if (existing) return;

    const mount = document.querySelector('[data-shogun-engraving-mount]');
    if (!mount) return;

    const tryAttach = () => {
      if (getRoot()) return true;

      const form =
        document.querySelector('form[action="/cart/add"]') ||
        document.querySelector('form[action*="/cart/add"]');
      if (!form) return false;

      if (!form.getAttribute('id')) form.setAttribute('id', 'shogun-product-form');
      const formId = form.getAttribute('id');
      mount.dataset.formId = formId;

      const rendered = mount.querySelector('[data-product-engraving]');
      if (rendered) {
        rendered.dataset.formId = formId;
        rendered.querySelectorAll('[form]').forEach((input) => {
          input.setAttribute('form', formId);
        });
        initUi(rendered, config);
        attachStandaloneFormHandler(rendered);
        return true;
      }
      return false;
    };

    if (tryAttach()) return;

    const observer = new MutationObserver(() => {
      if (tryAttach()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), 15000);
  }

  function init() {
    const config = readConfig();
    if (!config) return;

    const root = getRoot();
    if (root) {
      initUi(root, config);
      attachStandaloneFormHandler(root);
    }

    initShogunSupport(config);
  }

  window.ProductEngraving = {
    isActive: isEngravingActive,
    buildPairedFormData,
    buildItemsPayload,
    refreshUid,
    getConfig: readConfig,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
